import React, { useState, useEffect } from "react";
import { IconButton, InputBase, Paper, Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { IoSend, IoAttach } from "react-icons/io5";
import { useSelector } from "react-redux";
import { ClientEnglishFallback } from "../../constant/ClientEnglishFallback";


const ChatInput = ({ onSendMessage, selectedImages, setSelectedImages, error, setError, chatGroupId }) => {
  const { multiLanguageData } = useSelector((state) => state?.main);
  const LANGUAGE = ClientEnglishFallback.ClientChat(multiLanguageData?.ClientChat || {});
  const { TEXT_TYPE_A_MESSAGE } = LANGUAGE;
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setNewMessage("");
  }, [chatGroupId]);

  const handleSendMessage = async () => {
    setError("");
    if (!newMessage.trim() && selectedImages.length === 0) {
      setError("Please type a message or select at least one image.");
      return;
    }
    setIsLoading(true);
    try {
      await onSendMessage({ text: newMessage, images: selectedImages });
      setNewMessage("");
      setSelectedImages([]);
    } catch (err) {
      setError("Failed to send message.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = (e) => {
    setError("");
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length !== files.length) {
      setError("Only image files are allowed.");
      return;
    }
    setSelectedImages((prev) => [...prev, ...imageFiles]);
  };

  return (
    <Box sx={{ p: 2, bgcolor: "transparent" }}>
      <Paper
        component="form"
        elevation={3}
        sx={{
          display: "flex",
          alignItems: "center",
          p: "8px 16px",
          bgcolor: "#ffffff",
          borderRadius: "24px",
          border: "1px solid #e0e0e0",
          transition: "box-shadow 0.3s ease",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
          }
        }}>
        <IconButton
          component="label"
          disabled={isLoading}
          sx={{
            color: "#666",
            "&:hover": { color: "#1976d2", bgcolor: "rgba(25, 118, 210, 0.08)" }
          }}
        >
          <IoAttach size={26} />
          <input type="file" hidden multiple accept="image/*" onChange={handleImageChange} />
        </IconButton>

        <InputBase
          sx={{
            flex: 1,
            ml: 2,
            fontSize: "0.95rem",
            color: "#333",
            "& input::placeholder": {
              color: "#888",
              opacity: 1
            }
          }}
          placeholder={TEXT_TYPE_A_MESSAGE}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSendMessage())}
          disabled={isLoading}
          multiline
          maxRows={3}
        />

        <IconButton
          onClick={handleSendMessage}
          disabled={isLoading || (!newMessage.trim() && selectedImages.length === 0)}
          sx={{
            bgcolor: "#1976d2",
            color: "white",
            ml: 1,
            width: 40,
            height: 40,
            transition: "all 0.2s",
            "&:hover": { bgcolor: "#1565c0", transform: "scale(1.05)" },
            "&:disabled": { bgcolor: "#e0e0e0", color: "#999" }
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" thickness={5} />
          ) : <IoSend size={18} style={{ marginLeft: 2 }} />}
        </IconButton>
      </Paper>
      {error && (
        <Box sx={{ color: '#d32f2f', mt: 1, ml: 2, fontSize: "0.85rem", fontWeight: 500 }}>{error}</Box>
      )}
    </Box>
  );
};

export default ChatInput;
